import type { ThreeEvent } from '@react-three/fiber'
import { useAppSelector } from 'common/hooks/redux'
import { ElementIdLabel } from 'features/Procedure/procedure-model/element-id-label'
import {
  getSurfaceNormalAtPoint,
  getSurfaceNormalFromViewRay,
} from 'features/Procedure/procedure-model/lines'
import { type DrawingElement } from 'features/Procedure/state/procedure.type'
import { getXStrokeWidthForSize } from 'features/Procedure/utils/drawingElementFactory'
import { formatDrawingNoteDisplay } from 'features/Procedure/utils/drawingDescription.utils'
import { getProcedureOpacity } from 'features/Procedure/utils/utils'
import React, { useMemo, useRef } from 'react'
import type { MeshStandardMaterial, Object3D } from 'three'
import { Quaternion, Vector3 } from 'three'

type Props = {
  element: DrawingElement
  bodyMesh?: Object3D
  isSelected?: boolean
  isFromSelectedProcedure?: boolean
  showElementIds?: boolean
  onClick?: (event: ThreeEvent<PointerEvent>) => void
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void
  onPointerEnter?: (event: ThreeEvent<PointerEvent>) => void
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void
  useDepthTest?: boolean
  inactiveOpacity?: number
  interactive?: boolean
}

export const XPin = ({
  element,
  bodyMesh,
  isSelected = false,
  isFromSelectedProcedure = false,
  showElementIds: showElementIdsOverride,
  onClick,
  onPointerDown,
  onPointerEnter,
  onPointerOver,
  useDepthTest = false,
  inactiveOpacity = 0,
  interactive = true,
}: Props) => {
  const storeShowElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
  const isPrinting = useAppSelector((state) => state.procedureReducer.isPrinting)
  const mat1Ref = useRef<MeshStandardMaterial>(null)
  const mat2Ref = useRef<MeshStandardMaterial>(null)
  const showElementIds = showElementIdsOverride ?? storeShowElementIds

  React.useEffect(() => {
    const opacity = getProcedureOpacity(isFromSelectedProcedure, isPrinting, false, inactiveOpacity)
    const transparent = !isFromSelectedProcedure && !isPrinting
    ;[mat1Ref, mat2Ref].forEach((ref) => {
      if (ref.current) {
        ref.current.transparent = transparent
        ref.current.opacity = opacity
        ref.current.depthTest = useDepthTest
        ref.current.needsUpdate = true
      }
    })
  }, [isFromSelectedProcedure, isPrinting, useDepthTest, inactiveOpacity])

  const center = useMemo(
    () => new Vector3(element.points[0].x, element.points[0].y, element.points[0].z),
    [element.points[0].x, element.points[0].y, element.points[0].z],
  )

  const cameraAtDraw = useMemo(() => {
    const p = element.camera?.position
    if (p && typeof p.x === 'number' && typeof p.y === 'number' && typeof p.z === 'number') {
      return new Vector3(p.x, p.y, p.z)
    }
    return null
  }, [element.camera?.position])

  // Compute surface normal so the X lies flat on the body like a sticker.
  // Prefer view-ray normal (same face as at click) so hollows like the eye socket
  // don't get a wrong normal from body-center raycasts. Fallback: getSurfaceNormalAtPoint.
  const { groupPosition, groupQuaternion } = useMemo(() => {
    const SURFACE_OFFSET = 0.015
    let normal: Vector3 | null = null
    if (bodyMesh && cameraAtDraw) {
      normal = getSurfaceNormalFromViewRay(bodyMesh, center, cameraAtDraw)
    }
    if (!normal && bodyMesh) {
      normal = getSurfaceNormalAtPoint(bodyMesh, center)
    }
    if (!normal) {
      normal = new Vector3(0, 1, 0)
    }

    // Offset the group slightly outward along the normal to avoid z-fighting
    const pos = center.clone().addScaledVector(normal, SURFACE_OFFSET)

    // Rotate so the group's local +Z aligns with the surface outward normal.
    // The X arms (in the local XY plane) then lie flat on the body surface.
    const q = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), normal)

    return { groupPosition: pos, groupQuaternion: q }
  }, [bodyMesh, center, cameraAtDraw])

  const handleClick = (event: ThreeEvent<PointerEvent>) => {
    if (onClick) {
      event.stopPropagation()
      onClick(event)
    }
  }

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    onPointerDown?.(event)
  }

  const handlePointerEnter = (event: ThreeEvent<PointerEvent>) => {
    onPointerEnter?.(event)
  }

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    onPointerOver?.(event)
  }

  const descriptionText = useMemo(
    () => formatDrawingNoteDisplay(element.description),
    [element.description],
  )

  const size = element.size || 0.25
  // armLength = pin diameter so the X spans the same visual circle as the Pin sphere
  const armLength = size * 2
  // stroke width matches the free-line width at the same configurator step
  // getXStrokeWidthForSize maps pinSizes (0.1–0.25) → lineSizes (2–5); divide by 100 for world units
  const armStroke = getXStrokeWidthForSize(size) / 100
  // nearly flat in Z (= surface normal direction) so it sits flush on the body
  const armDepth = 0.001
  // Square hit target (armLength ≈ pin diameter): fills gaps between the two bars.
  // Slightly recessed along local −Z so rays on the strokes still hit the boxes first.
  const pickSquareZ = -Math.max(armDepth * 4, 0.002)

  const opacity = getProcedureOpacity(isFromSelectedProcedure, isPrinting, false, inactiveOpacity)
  const transparent = !isFromSelectedProcedure && !isPrinting

  const materialProps = {
    color: element.color,
    emissive: element.color,
    emissiveIntensity: isSelected ? 0.3 : 0.1,
    transparent,
    opacity,
    depthTest: useDepthTest,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -2,
  } as const

  const pointerHandlers = interactive
    ? {
        onClick: handleClick,
        onPointerDown: handlePointerDown,
        onPointerEnter: handlePointerEnter,
        onPointerOver: handlePointerOver,
      }
    : {}

  return (
    <group position={groupPosition} quaternion={groupQuaternion}>
      {interactive && (
        <mesh position={[0, 0, pickSquareZ]} {...pointerHandlers}>
          <planeGeometry args={[armLength, armLength]} />
          <meshBasicMaterial transparent opacity={0} depthTest={useDepthTest} depthWrite={false} />
        </mesh>
      )}
      <mesh rotation={[0, 0, Math.PI / 4]} {...pointerHandlers}>
        <boxGeometry args={[armLength, armStroke, armDepth]} />
        <meshStandardMaterial ref={mat1Ref} {...materialProps} />
      </mesh>
      <mesh rotation={[0, 0, -Math.PI / 4]} {...pointerHandlers}>
        <boxGeometry args={[armLength, armStroke, armDepth]} />
        <meshStandardMaterial ref={mat2Ref} {...materialProps} />
      </mesh>
      {showElementIds &&
        descriptionText &&
        (showElementIdsOverride === true || isFromSelectedProcedure) && (
        <ElementIdLabel
          text={descriptionText}
          position={[0, armLength / 2, 0]}
          color={element.color}
          useDepthTest={false}
          onClick={onClick}
          onPointerDown={onPointerDown}
          onPointerEnter={onPointerEnter}
          onPointerOver={onPointerOver}
          offset={{
            x: Math.abs(center.x) > Math.abs(center.z) ? (center.x > 0 ? 0.8 : -0.8) : 0,
            y: 0.25,
            z: Math.abs(center.z) >= Math.abs(center.x) ? (center.z >= 0 ? 0.8 : -0.8) : 0,
          }}
        />
      )}
    </group>
  )
}
